/**
 * English locale data for natural forms.
 * Key = atomic number.
 * Only locale-dependent fields: description, appearance, mainMinerals names, spriteLabels.
 */

export interface NaturalFormLocaleData {
  description: string;
  appearance: string;
  mainMinerals: { name: string }[];
  spriteLabels?: string[];
}

export const naturalFormsDataEn: Record<number, NaturalFormLocaleData> = {
  1: {
    description: "Hydrogen does not exist in native form on Earth. It is present as gas (H₂) in trace amounts in the atmosphere and mainly in water and organic compounds.",
    appearance: "Colourless, odourless, extremely light gas",
    mainMinerals: [],
    spriteLabels: ['H discharge tube (pink-violet)'],
  },
  2: {
    description: "Helium is a rare noble gas in the atmosphere (5 ppm). It is trapped in natural gas deposits, notably in the United States and Qatar, where it forms by radioactive decay.",
    appearance: "Colourless, odourless gas (emits yellow light under electrical discharge)",
    mainMinerals: [],
    spriteLabels: ['He discharge tube (yellow-orange)'],
  },
  3: {
    description: "Lithium is never found in native form (too reactive). It is extracted from spodumene, lepidolite and brine from salt lakes (Atacama, Bolivia). Global demand is soaring with lithium-ion batteries.",
    appearance: "Solid white-silver metal, very light (less dense than water)",
    mainMinerals: [
      { name: "Spodumene" },
      { name: "Lepidolite" },
      { name: "Petalite" },
    ],
    spriteLabels: ['Lepidolite (purple mica)', 'Kunzite (pink spodumene)'],
  },
  4: {
    description: "Beryllium is found in beryl, a silicate that produces precious gemstones depending on its impurities: emerald (green, with Cr), aquamarine (blue, with Fe), and heliodor (yellow). Chrysoberyl is another source.",
    appearance: "Prismatic hexagonal crystals, often intense green (emerald) or blue-green (aquamarine)",
    mainMinerals: [
      { name: "Beryl" },
      { name: "Emerald (variety)" },
      { name: "Aquamarine (variety)" },
      { name: "Chrysoberyl" },
    ],
    spriteLabels: ['Emerald (Be₃Al₂Si₆O₁₈)', 'Aquamarine (Be₃Al₂Si₆O₁₈)', 'Alexandrite (BeAl₂O₄)'],
  },
  5: {
    description: "Boron is found in evaporites such as borax and kernite, formed by evaporation of salt lakes. Death Valley (California) and Turkey (Eskişehir) are the world's main sources.",
    appearance: "Borax: white masses with prismatic crystals, powdery or glassy appearance",
    mainMinerals: [
      { name: "Borax" },
      { name: "Kernite" },
      { name: "Colemanite" },
    ],
    spriteLabels: ['Black tourmaline (B/Fe)', 'Rubellite (pink tourmaline)'],
  },
  6: {
    description: "Carbon exists in two natural allotropes: graphite (black hexagonal layers, soft) and diamond (cubic lattice, transparent and ultra-hard). It is also present in coals and carbonates. Graphite is mined in China and India; diamonds come mainly from southern Africa and Russia.",
    appearance: "Graphite: greasy black metallic layers. Diamond: colourless to brilliant octahedral crystals.",
    mainMinerals: [
      { name: "Graphite" },
      { name: "Diamond" },
      { name: "Coal / anthracite" },
    ],
    spriteLabels: ['Graphite (C)', 'Amber (C)', 'Raw diamond (C)'],
  },
  7: {
    description: "Nitrogen makes up 78% of the atmosphere as gaseous N₂. It does not form significant minerals at the surface, but is present in niter (saltpetre) historically used in gunpowder.",
    appearance: "Colourless, odourless gas — main constituent of air",
    mainMinerals: [
      { name: "Niter (saltpetre)" },
    ],
    spriteLabels: ['N discharge tube (pink-lavender)'],
  },
  8: {
    description: "Oxygen is the second atmospheric gas (21%) and the most abundant element in the Earth's crust. It is present in nearly all minerals and rocks as oxides, silicates and carbonates.",
    appearance: "Colourless and odourless gas at ambient temperature",
    mainMinerals: [
      { name: "Water" },
      { name: "Quartz" },
      { name: "Hematite" },
    ],
    spriteLabels: ['O discharge tube (blue-white)'],
  },
  9: {
    description: "Fluorine is a highly reactive gas, never found in native form. The main fluoride mineral is fluorite (fluorspar), a very common purple-to-green cube. It is also found in apatite and cryolite (rare, Greenland).",
    appearance: "Corrosive yellow-green gas. Fluorite: cubic crystals in purple, green or colourless.",
    mainMinerals: [
      { name: "Fluorite (fluorspar)" },
      { name: "Fluorapatite" },
      { name: "Cryolite" },
    ],
    spriteLabels: ['Fluorite (CaF₂)', 'F discharge tube (pale yellow)'],
  },
  10: {
    description: "Neon is a rare atmospheric noble gas (18 ppm). It forms no chemical compounds. Extracted by fractional distillation of liquid air.",
    appearance: "Colourless gas (emits characteristic orange light under electrical discharge)",
    mainMinerals: [],
    spriteLabels: ['Neon tube Ne (orange-red)'],
  },
  11: {
    description: "Sodium is never found in native form (too reactive with water). Halite (rock salt, NaCl) is one of the most common minerals, formed by evaporation of ancient seas. Salt deposits are found worldwide, notably in Poland, the United States and Pakistan (Khewra).",
    appearance: "Halite: white to colourless cubic crystals, transparent, with characteristic salty taste",
    mainMinerals: [
      { name: "Halite (rock salt)" },
      { name: "Trona" },
      { name: "Natron" },
    ],
    spriteLabels: ['Lapis lazuli (Na/Ca silicate)', 'Jadeite (NaAlSi₂O₆)'],
  },
  12: {
    description: "Magnesium is a very abundant metal in the Earth's crust. Magnesite and dolomite are its main ores. It is also present in olivine (green volcanic rock) and serpentinite.",
    appearance: "Magnesite: compact white masses. Dolomite: white to pink rhombohedra.",
    mainMinerals: [
      { name: "Magnesite" },
      { name: "Dolomite" },
      { name: "Olivine" },
    ],
    spriteLabels: ['Malachite (Cu₂CO₃(OH)₂)', 'Peridot (olivine Mg₂SiO₄)', 'Red spinel (MgAl₂O₄)'],
  },
  13: {
    description: "Aluminium is the most abundant metal in the Earth's crust. Extracted from bauxite, a mixture of hydrated aluminium oxides formed by intense tropical weathering. The largest deposits are in Australia, Guinea and Brazil. Pure corundum gives ruby (red) and sapphire (blue).",
    appearance: "Bauxite: reddish to ochre rock, in earthy or pisolitic masses (round grains). Corundum: hard hexagonal crystals.",
    mainMinerals: [
      { name: "Gibbsite (bauxite)" },
      { name: "Boehmite (bauxite)" },
      { name: "Corundum" },
      { name: "Ruby / Sapphire (varieties)" },
    ],
    spriteLabels: ['Ruby (Al₂O₃ + Cr)', 'Sapphire (Al₂O₃ + Fe/Ti)', 'Topaz (Al₂SiO₄F₂)'],
  },
  14: {
    description: "Silicon is the second most abundant element in the Earth's crust. Its purest natural form is quartz (SiO₂), present in almost all rocks. Its coloured varieties include amethyst (purple), citrine (yellow), and rose quartz.",
    appearance: "Quartz: colourless to milky white hexagonal prismatic crystals terminated by pyramids",
    mainMinerals: [
      { name: "Quartz" },
      { name: "Amethyst (variety)" },
      { name: "Feldspar" },
      { name: "Mica" },
    ],
    spriteLabels: ['Rose quartz (SiO₂)', 'Opal (SiO₂)'],
  },
  15: {
    description: "Phosphorus is not found in native form. Extracted mainly from apatite in marine phosphorite rocks. Morocco holds 70% of world reserves. Essential for agricultural fertilisers.",
    appearance: "Apatite: hexagonal crystals in green, blue or brown, sometimes colourless, translucent",
    mainMinerals: [
      { name: "Fluorapatite" },
      { name: "Hydroxyapatite" },
      { name: "Phosphorite (rock)" },
    ],
    spriteLabels: ['White phosphorus (cold glow)'],
  },
  16: {
    description: "Sulfur is found in native form as bright yellow crystals around volcanoes, fumaroles and evaporite deposits. It is one of the few elements found pure in nature. Sicily, Indonesia and Mexico are classic sources.",
    appearance: "Bright yellow orthorhombic crystals, often in pyramids or granular masses, around volcanic craters",
    mainMinerals: [
      { name: "Native sulfur" },
      { name: "Pyrite" },
      { name: "Chalcopyrite" },
    ],
    spriteLabels: ['Native sulfur (S)'],
  },
  17: {
    description: "Chlorine is a highly toxic yellowish gas, never in native form. It is ubiquitous as chlorides in halite (rock salt), oceans and saline groundwater.",
    appearance: "Toxic yellow-green gas. Halite: white to colourless cubic crystals.",
    mainMinerals: [
      { name: "Halite" },
      { name: "Sylvite" },
      { name: "Atacamite" },
    ],
    spriteLabels: ['Cl discharge tube (yellow-green)'],
  },
  18: {
    description: "Argon is the third atmospheric gas (0.93%). A noble gas, it forms no chemical compounds. Extracted by fractional distillation of liquid air.",
    appearance: "Colourless, odourless gas (emits violet-blue light under electrical discharge)",
    mainMinerals: [],
    spriteLabels: ['Ar discharge tube (violet-mauve)'],
  },
  19: {
    description: "Potassium is not found in native form. Sylvite (KCl) and carnallite are the main ores, formed in marine evaporites. Canada (Saskatchewan), Germany and Russia are major producers. Indispensable as an agricultural fertiliser.",
    appearance: "Sylvite: colourless to white or red cubic crystals, sharp taste",
    mainMinerals: [
      { name: "Sylvite" },
      { name: "Carnallite" },
      { name: "Potassic feldspar" },
    ],
    spriteLabels: ['Sylvite (KCl, pink crystals)'],
  },
  20: {
    description: "Calcium is very abundant in the Earth's crust. Calcite is one of the most common minerals, present in limestones, marbles, stalactites. Gypsum forms from evaporation of ancient seas. Aragonite constitutes shells and corals.",
    appearance: "Calcite: white to colourless rhombohedral crystals, perfect cleavage, translucent. Gypsum: silky flakes.",
    mainMinerals: [
      { name: "Calcite" },
      { name: "Aragonite" },
      { name: "Gypsum" },
      { name: "Fluorite" },
    ],
    spriteLabels: ['Fossil coral (CaCO₃)', 'Tanzanite (zoisite Ca/Al)'],
  },
  21: {
    description: "Scandium is a rare transition metal, dispersed in trace amounts in many minerals. Thortveitite is the only ore truly concentrated in scandium. Ukraine and China are the main producers. Used in high-performance aluminium alloys.",
    appearance: "Thortveitite: grey-green to brown prismatic crystals, rare and little known to the general public",
    mainMinerals: [
      { name: "Thortveitite" },
      { name: "Kolbeckite" },
    ],
    spriteLabels: ['Thortveitite (Sc₂Si₂O₇)'],
  },
  22: {
    description: "Titanium is found mainly in rutile (TiO₂, brown-red) and ilmenite (FeTiO₃, black), opaque minerals extracted from coastal mineral sands. Australia, South Africa and China are the main producers. Titanium metal is lightweight and strong.",
    appearance: "Rutile: brown-red to black tetragonal crystals, metallic, often in knee-twin forms",
    mainMinerals: [
      { name: "Rutile" },
      { name: "Ilmenite" },
      { name: "Anatase" },
    ],
    spriteLabels: ['Rutile (TiO₂)'],
  },
  23: {
    description: "Vanadium is found in many minerals at low concentrations. Vanadinite forms beautiful hexagonal red-orange crystals. It is often recovered as a by-product of petroleum refining or iron smelting.",
    appearance: "Vanadinite: hexagonal prismatic red-orange to brown crystals, shiny",
    mainMinerals: [
      { name: "Vanadinite" },
      { name: "Carnotite" },
      { name: "Roscoelite" },
    ],
    spriteLabels: ['Vanadinite (Pb₅(VO₄)₃Cl)'],
  },
  24: {
    description: "Chromium is found mainly in chromite, a brown-black to metallic spinel. South Africa (Bushveld) and Kazakhstan hold the largest deposits. Crocoite, bright red, is a spectacular collector's mineral.",
    appearance: "Chromite: black-brownish masses with metallic lustre, granular. Crocoite: bright red prismatic crystals.",
    mainMinerals: [
      { name: "Chromite" },
      { name: "Crocoite" },
    ],
    spriteLabels: ['Polished chrome (Cr)'],
  },
  25: {
    description: "Manganese is found mainly in pyrolusite (MnO₂), a dull black oxide. It is also found in polymetallic nodules on the ocean floor and in pink rhodonite. South Africa, Australia and Gabon are the main producers.",
    appearance: "Pyrolusite: dull black masses, as black dendrites on rocks (like frost) or opaque",
    mainMinerals: [
      { name: "Pyrolusite" },
      { name: "Manganite" },
      { name: "Rhodonite" },
      { name: "Rhodochrosite" },
    ],
    spriteLabels: ['Amethyst (SiO₂+Fe)', 'Rhodonite (MnSiO₃)'],
  },
  26: {
    description: "Iron is the most abundant metal in the Earth (terrestrial core). In the crust, it is found as oxides (red hematite, black magnetite) and sulfides (pyrite, 'fool's gold'). BIFs (banded iron formations) from Brazil, Australia and Canada are the main sources.",
    appearance: "Hematite: red-grey masses with metallic lustre. Magnetite: black magnetic masses. Pyrite: bright golden cubes.",
    mainMinerals: [
      { name: "Hematite" },
      { name: "Magnetite" },
      { name: "Pyrite (fool's gold)" },
      { name: "Siderite" },
    ],
    spriteLabels: ['Hematite (Fe₂O₃)', 'Pyrite (FeS₂)', 'Almandine garnet (Fe₃Al₂Si₃O₁₂)'],
  },
  27: {
    description: "Cobalt is found mainly in cobaltite and erythrite (cobalt bloom, bright pink). It is often extracted as a by-product of copper and nickel. The DRC (Congo) holds 70% of world reserves, essential for lithium-ion batteries.",
    appearance: "Cobaltite: metallic silver-grey cubic crystals. Erythrite: characteristic bright pink efflorescences.",
    mainMinerals: [
      { name: "Cobaltite" },
      { name: "Erythrite (cobalt bloom)" },
      { name: "Linnaeite" },
    ],
    spriteLabels: ['Erythrite (Co₃(AsO₄)₂·8H₂O)'],
  },
  28: {
    description: "Nickel is found mainly in pentlandite (bronze-brown sulfide) and nickeliferous laterites (tropical oxides). Native nickel-iron exists in meteorites. Indonesia, the Philippines and Russia are the main producers.",
    appearance: "Pentlandite: bronze masses in association with pyrite and pyrrhotite in basaltic rocks",
    mainMinerals: [
      { name: "Pentlandite" },
      { name: "Millerite" },
      { name: "Garnierite (laterite)" },
    ],
    spriteLabels: ['Iron meteorite (Fe/Ni)'],
  },
  29: {
    description: "Copper is one of the few metals found in native form (reddish nuggets). Its most beautiful oxidation ores are malachite (bright green) and azurite (intense blue). Chalcopyrite is the main economic ore. Chile provides 25% of world production.",
    appearance: "Native copper: reddish dendritic masses. Malachite: bright green with zonation. Azurite: intense blue. Chalcopyrite: iridescent golden-yellow.",
    mainMinerals: [
      { name: "Native copper" },
      { name: "Malachite" },
      { name: "Azurite" },
      { name: "Chalcopyrite" },
    ],
    spriteLabels: ['Native copper (Cu)', 'Azurite (Cu₃(CO₃)₂(OH)₂)', 'Chalcopyrite (CuFeS₂)', 'Turquoise (CuAl₆(PO₄)₄(OH)₈)'],
  },
  30: {
    description: "Zinc is found mainly in sphalerite (ZnS), a brown to black sulfide with a characteristic resinous lustre. Smithsonite (carbonate) can form beautiful blue or green spheres. China, Peru and Australia are the main producers.",
    appearance: "Sphalerite: brown-reddish to black tetrahedral crystals with resinous lustre, often with perfect cleavage",
    mainMinerals: [
      { name: "Sphalerite (blende)" },
      { name: "Smithsonite" },
      { name: "Hemimorphite" },
    ],
    spriteLabels: ['Bornite / Sphalerite (ZnS)'],
  },
  31: {
    description: "Gallium is a rare metal recovered as a by-product of bauxite (aluminium) and sphalerite (zinc) refining. It forms no abundant ore of its own; gallite and germanite contain trace amounts. China produces 80% of world gallium, indispensable for GaAs semiconductors and LEDs.",
    appearance: "Gallite (CuGaS₂): small grey metallic tetragonal crystals, indistinguishable without analysis. Gallium metal: silvery liquid slightly above ambient temperature (Tm = 29.8°C).",
    mainMinerals: [
      { name: "Gallite" },
      { name: "Germanite (with Ga)" },
      { name: "Sphalerite (with Ga)" },
    ],
    spriteLabels: ['Germanite/gallite (Ga)'],
  },
  32: {
    description: "Germanium is a dispersed metalloid, extracted as a by-product of zinc (sphalerite) and coal. Argyrodite, a rare silver mineral, was the ore in which Clemens Winkler discovered the element in 1886. China and the DRC are the main producers. Essential for optical fibres and infrared.",
    appearance: "Argyrodite: small cubic crystals with steel-grey metallic lustre, often in granular masses associated with silver",
    mainMinerals: [
      { name: "Argyrodite" },
      { name: "Sphalerite (with Ge)" },
      { name: "Renierite" },
    ],
    spriteLabels: ['Argyrodite (Ag₈GeS₆)'],
  },
  33: {
    description: "Arsenic is found in native form as stratified grey metallic masses. Its sulfides (orange-red realgar, yellow orpiment) are vividly coloured and historically used as pigments. Arsenopyrite is the main ore. Toxic.",
    appearance: "Native arsenic: lamellar grey metallic masses. Realgar: orange-red. Orpiment: golden-yellow.",
    mainMinerals: [
      { name: "Native arsenic" },
      { name: "Arsenopyrite" },
      { name: "Realgar" },
      { name: "Orpiment" },
    ],
    spriteLabels: ['Realgar (AsS)', 'Orpiment (As₂S₃)'],
  },
  34: {
    description: "Selenium occurs in minute quantities in copper and lead sulfides. It is recovered mainly as a by-product of electrolytic copper refining. It exists in native form as grey trigonal crystals or brick-red form.",
    appearance: "Native selenium: grey metallic trigonal crystals (α form) or brick-red (glassy form)",
    mainMinerals: [
      { name: "Clausthalite" },
      { name: "Naumannite" },
      { name: "Berzelianite" },
    ],
    spriteLabels: ['Native selenium (Se)'],
  },
  35: {
    description: "Bromine is one of only two liquid elements at ambient temperature along with mercury. It is not found in native form. It is extracted from natural brines rich in bromides (Dead Sea, oil wells in the United States).",
    appearance: "Dark brown-red fuming liquid, with pungent toxic odour",
    mainMinerals: [
      { name: "Bromargyrite" },
      { name: "Embolite" },
    ],
    spriteLabels: ['Fuming liquid bromine (brown-red)'],
  },
  36: {
    description: "Krypton is a rare atmospheric noble gas (1 ppm). Chemically inert, it forms no stable compounds at ambient temperature. Extracted by fractional distillation of liquid air. Used in high-performance incandescent bulbs and excimer lasers.",
    appearance: "Colourless gas (emits pale violet to white light under electrical discharge)",
    mainMinerals: [],
    spriteLabels: ['Kr discharge tube (pale violet)'],
  },
  37: {
    description: "Rubidium is a highly reactive alkali metal extracted as a by-product of lithium. It is present in trace amounts in lepidolite (purple lithium mica) and pollucite. South Africa and Zimbabwe are the main sources. Rubidiferous lepidolite shows a beautiful purple colour due to Mn and Fe ions.",
    appearance: "Rubidiferous lepidolite: purple to lilac micaceous flakes, pearlescent lustre, often in granular masses",
    mainMinerals: [
      { name: "Lepidolite (with Rb)" },
      { name: "Pollucite (with Rb)" },
    ],
    spriteLabels: ['Rubidiferous lepidolite (purple)'],
  },
  38: {
    description: "Strontium is found mainly in celestite (SrSO₄), with very characteristic sky-blue translucent tabular crystals. Strontianite (carbonate) is rarer. Mexico, China and Spain are the main producers. Used in pyrotechnics for the red colour.",
    appearance: "Celestite: sky-blue to white tabular crystals, translucent, often in geodes",
    mainMinerals: [
      { name: "Celestite" },
      { name: "Strontianite" },
    ],
    spriteLabels: ['Celestite (SrSO₄)'],
  },
  39: {
    description: "Yttrium is a light rare earth present in xenotime (YPO₄) and gadolinite. Xenotime forms characteristic brown-yellow to reddish-brown prismatic crystals. China provides most of world production. Yttrium is indispensable for red screen phosphors and YAG lasers.",
    appearance: "Xenotime (YPO₄): brown-yellow to reddish-brown tetragonal prismatic crystals, resinous to vitreous lustre",
    mainMinerals: [
      { name: "Xenotime" },
      { name: "Gadolinite" },
      { name: "Monazite (with Y)" },
    ],
    spriteLabels: ['Xenotime (YPO₄, brown-yellow)'],
  },
  40: {
    description: "Zirconium is found mainly in zircon (ZrSiO₄), one of the Earth's oldest minerals (4.4 Ga, Jack Hills, Australia). Very resistant to weathering, it is used in geochronological dating. Coloured varieties are popular gemstones.",
    appearance: "Zircon: colourless to brown tetragonal crystals, with brilliant adamantine lustre, very hard",
    mainMinerals: [
      { name: "Zircon" },
      { name: "Baddeleyite" },
    ],
    spriteLabels: ['Zircon (ZrSiO₄)'],
  },
  41: {
    description: "Niobium is found mainly in columbite (coltan), a heavy black oxide associated with tantalite. Brazil holds 90% of world reserves, essentially in the Araxa carbonatite complex. Indispensable for high-strength steels and superconducting magnets.",
    appearance: "Columbite: black prismatic to tabular crystals with subadamantine to metallic lustre, very heavy",
    mainMinerals: [
      { name: "Columbite-(Fe)" },
      { name: "Coltan (colombo-tantalite)" },
      { name: "Pyrochlore" },
    ],
    spriteLabels: ['Columbite (black metal)'],
  },
  42: {
    description: "Molybdenum is found mainly in molybdenite (MoS₂), a silvery-grey lamellar sulfide resembling graphite. It forms in porphyry copper deposits. China provides 40% of world production. Wulfenite, yellow-orange, is a rare gemstone.",
    appearance: "Molybdenite: flexible and shiny silvery-grey hexagonal flakes, metallic lustre",
    mainMinerals: [
      { name: "Molybdenite" },
      { name: "Wulfenite" },
    ],
    spriteLabels: ['Wulfenite (PbMoO₄)'],
  },
  43: {
    description: "Technetium is the first synthetic element of the periodic table. It does not exist in nature in measurable quantities (max half-life: 4.2 Ma for ⁹⁸Tc). Produced in nuclear reactors by uranium fission, it is crucial in nuclear medicine: ⁹⁹ᵐTc is used in 85% of medical scintigraphies worldwide.",
    appearance: "Bright silvery-grey metallic pellet in the laboratory, slight blue radioactive halo, cyclotron target",
    mainMinerals: [],
    spriteLabels: ['Technetium metal (lab pellet)'],
  },
  44: {
    description: "Ruthenium is an extremely rare transition metal from the platinum group. It is found in trace amounts in platinum ores, mainly as laurite (RuS₂). Russia and South Africa are the main producers. Used as a catalyst and in electrical contacts.",
    appearance: "Laurite: small black cubic crystals with metallic lustre, associated with platinum ores",
    mainMinerals: [
      { name: "Laurite" },
      { name: "Native ruthenium" },
    ],
    spriteLabels: ['Laurite (RuS₂)'],
  },
  45: {
    description: "Rhodium is one of the rarest and most precious metals in the world. It is found in minute quantities in platinum and nickel ores. South Africa produces 80%. Essential for automotive catalytic converters (NOx converters).",
    appearance: "Native rhodium: small brilliant silvery grains or nuggets, extremely dense, associated with platinum",
    mainMinerals: [
      { name: "Native rhodium" },
      { name: "Hollingworthite" },
    ],
    spriteLabels: ['Native rhodium (Rh)'],
  },
  46: {
    description: "Palladium is a precious platinum-group metal, discovered in 1803. It is found in native form as small nuggets or alloyed with platinum. South Africa and Russia provide 80% of production. Indispensable for catalytic converters and electronics.",
    appearance: "Native palladium: brilliant silvery nuggets, lighter than platinum (density 12 vs 21)",
    mainMinerals: [
      { name: "Native palladium" },
      { name: "Stibiopalladinite" },
      { name: "Braggite" },
    ],
    spriteLabels: ['Native palladium (Pd)'],
  },
  47: {
    description: "Silver is found in native form as filaments, flakes or dendrites of brilliant white. Argentite and pyrargyrite are also important ores. Mexico, Peru and China are the main producers. Silver often accompanies copper and lead ores.",
    appearance: "Native silver: filiform, lamellar or dendritic brilliant white metallic masses",
    mainMinerals: [
      { name: "Native silver" },
      { name: "Argentite / Acanthite" },
      { name: "Pyrargyrite (red silver)" },
      { name: "Chlorargyrite" },
    ],
    spriteLabels: ['Native silver (Ag)'],
  },
  48: {
    description: "Cadmium is a toxic metal present in trace amounts in zinc ores (sphalerite). Greenockite (CdS) is its only proper mineral, with a characteristic bright yellow colour. It is recovered as a by-product of zinc refining. Used in NiCd batteries and yellow pigments.",
    appearance: "Greenockite: bright yellow to orange powder or crusts on sphalerite, adamantine lustre",
    mainMinerals: [
      { name: "Greenockite" },
      { name: "Otavite" },
    ],
    spriteLabels: ['Greenockite (CdS)'],
  },
  49: {
    description: "Indium is a very rare metal recovered exclusively as a by-product of zinc (sphalerite) refining. It forms no exploitable ore of its own. China produces 60% of world indium. Indispensable for ITO (indium tin oxide), the transparent conductive coating on LCD and OLED screens.",
    appearance: "Indium metal: brilliant silvery-white ingots slightly bluish, very soft (scratchable with a fingernail), emits a tin cry",
    mainMinerals: [
      { name: "Indium in sphalerite" },
      { name: "Roquesite" },
    ],
    spriteLabels: ['Indium metal (tin white)'],
  },
  50: {
    description: "Tin is found mainly in cassiterite, a brown-red to black oxide. Cassiterite mining dates back 3000 years BC (Bronze = Cu + Sn). China, Indonesia and Myanmar are the main producers.",
    appearance: "Cassiterite: brown-red to black tetragonal crystals, with brilliant adamantine lustre, very heavy",
    mainMinerals: [
      { name: "Cassiterite" },
      { name: "Stannite" },
    ],
    spriteLabels: ['Cassiterite (SnO₂)'],
  },
  51: {
    description: "Antimony is found mainly in stibnite (Sb₂S₃), a sulfide with characteristic silver prismatic crystals. Native antimony also exists in small quantities. China provides 85% of world production.",
    appearance: "Stibnite: elongated and striated grey-steel prismatic crystals with brilliant metallic lustre",
    mainMinerals: [
      { name: "Stibnite (antimonite)" },
      { name: "Native antimony" },
      { name: "Boulangerite" },
    ],
    spriteLabels: ['Crystallised antimony (Sb)'],
  },
  52: {
    description: "Tellurium is a rare metalloid, extracted as a by-product of copper refining. It is found in native tellurides and gold-bearing tellurites (calaverite, sylvanite). These gold tellurides caused the Kalgoorlie gold rush (Australia, 1893).",
    appearance: "Native tellurium: white-silver brilliant trigonal crystals, longitudinally striated",
    mainMinerals: [
      { name: "Native tellurium" },
      { name: "Calaverite" },
      { name: "Sylvanite" },
    ],
    spriteLabels: ['Calaverite (AuTe₂)'],
  },
  53: {
    description: "Iodine is found mainly in saline groundwater and Chilean nitrate deposits (caliche). Lautarite and iodyrite are the main minerals. Chile and Japan are the main producers. Iodine can form grey-violet metallic crystals.",
    appearance: "Native iodine: grey-violet metallic rhombohedral crystals, subliming to violet vapour",
    mainMinerals: [
      { name: "Iodyrite (iodargyrite)" },
      { name: "Lautarite" },
    ],
    spriteLabels: ['Native iodine (I₂)'],
  },
  54: {
    description: "Xenon is a rare atmospheric noble gas (0.087 ppm). Despite its chemical inertness, it forms a few compounds with fluorine. Extracted by fractional distillation of liquid air. Used in xenon (HID) headlights, excimer lasers and spacecraft ion thrusters.",
    appearance: "Colourless gas (emits intense blue-white light under electrical discharge)",
    mainMinerals: [],
    spriteLabels: ['Xe discharge tube (blue-white)'],
  },
  55: {
    description: "Caesium is a soft, silvery alkali metal that ignites spontaneously in air. Its main ore is pollucite, a rare silicate found mainly in Manitoba (Canada) and Namibia. Caesium-137 (radioactive) is a by-product of nuclear reactors. Used in atomic clocks.",
    appearance: "Pollucite: colourless to white glassy masses, glass or quartz appearance, no cleavage",
    mainMinerals: [
      { name: "Pollucite" },
    ],
    spriteLabels: ['Pollucite (Cs silicate)'],
  },
  56: {
    description: "Barium is found mainly in barite (BaSO₄), a very dense white sulfate (density 4.5). Its high density makes it indispensable as a drilling fluid in the oil industry. It forms 'desert roses' in deserts.",
    appearance: "Barite: white to colourless tabular crystals, in rosettes or crystal crests, surprisingly heavy",
    mainMinerals: [
      { name: "Barite (barytine)" },
      { name: "Witherite" },
    ],
    spriteLabels: ['Barite (BaSO₄)'],
  },
  57: {
    description: "Lanthanum is present in rare earth minerals, mainly monazite and bastnäsite. Monazite is often found in coastal sands (Kerala, India; Brazil). China holds 60% of world rare earth reserves.",
    appearance: "Bastnäsite: brown-yellow hexagonal crystals with vitreous lustre. Monazite: rounded brown-orange grains.",
    mainMinerals: [
      { name: "Monazite" },
      { name: "Bastnäsite" },
      { name: "Allanite" },
    ],
    spriteLabels: ['Bastnäsite (La/Ce)'],
  },
  58: {
    description: "Cerium is the most abundant rare earth. It is found in monazite and bastnäsite, minerals of carbonatites and coastal sands. China, India and Brazil are the main producers.",
    appearance: "Monazite: brown-orange monoclinic crystals, often as rounded grains in mineral sands",
    mainMinerals: [
      { name: "Monazite" },
      { name: "Bastnäsite" },
      { name: "Allanite" },
    ],
    spriteLabels: ['Monazite (Ce/La PO₄)'],
  },
  59: {
    description: "Praseodymium is a rare earth present in monazite and bastnäsite. Its bastnäsite has a beautiful characteristic green colour due to Pr³⁺ ions. Used in special glasses filtering yellow light for welders and in NdFeB permanent magnets.",
    appearance: "Pr-bearing bastnäsite: green to yellow-green hexagonal crystals, vitreous to resinous lustre",
    mainMinerals: [
      { name: "Monazite" },
      { name: "Bastnäsite" },
    ],
    spriteLabels: ['Bastnäsite (Pr, green)'],
  },
  60: {
    description: "Neodymium is the most industrially used rare earth. Its NdFeB (neodymium-iron-boron) magnets are the most powerful in the world and equip electric motors and wind turbines. Extracted from monazite and bastnäsite, mainly in China (85% of production).",
    appearance: "Neodymium metal: brilliant silvery ingots slightly bluish, rapidly oxidising to purple on the surface",
    mainMinerals: [
      { name: "Monazite" },
      { name: "Bastnäsite" },
    ],
    spriteLabels: ['Neodymium metal (Nd)'],
  },
  61: {
    description: "Promethium is the only rare earth with no stable isotope. All its isotopes are radioactive, the longest-lived (¹⁴⁵Pm) having a half-life of 17.7 years. Produced in very small quantities in nuclear reactors. Used in miniature beta energy generators (pacemakers, spacecraft) and luminescent paints.",
    appearance: "Sealed glass vial containing a pink-violet luminescent powder, gentle cold radioactive glow",
    mainMinerals: [],
    spriteLabels: ['Promethium (luminescent vial)'],
  },
  62: {
    description: "Samarium is a rare earth present in monazite and bastnäsite. Sm-bearing monazite has a characteristic beige-brown tint. Known for its SmCo (samarium-cobalt) magnets, the most resistant to thermal demagnetisation. China is the main producer.",
    appearance: "Sm-bearing monazite: beige-brown monoclinic crystals, sometimes orange, vitreous to resinous lustre",
    mainMinerals: [
      { name: "Monazite" },
      { name: "Bastnäsite" },
    ],
    spriteLabels: ['Sm-bearing monazite (beige-brown)'],
  },
  63: {
    description: "Europium is the most reactive rare earth, oxidising instantly in air. It is found in trace amounts in bastnäsite and monazite. Its Eu²⁺ and Eu³⁺ ions emit intense red and blue luminescences, essential for screen phosphors and anti-counterfeiting banknotes.",
    appearance: "Eu-bearing bastnäsite: cream to pale yellow hexagonal crystals, vitreous lustre, indistinguishable without chemical analysis",
    mainMinerals: [
      { name: "Bastnäsite (with Eu)" },
      { name: "Monazite (with Eu)" },
    ],
    spriteLabels: ['Eu bastnäsite (cream-yellow)'],
  },
  64: {
    description: "Gadolinium is a rare earth with exceptional magnetic properties. Gadolinite, a dense dark-green-black mineral, is its main historical source. Its Gd³⁺ ions are used as MRI contrast agents. China dominates world production.",
    appearance: "Gadolinite: compact dark-green-black masses with resinous to vitreous lustre, often metamict (amorphous due to radioactivity)",
    mainMinerals: [
      { name: "Gadolinite" },
      { name: "Monazite (with Gd)" },
    ],
    spriteLabels: ['Gadolinite (dark green-black)'],
  },
  65: {
    description: "Terbium is a rare earth extracted from xenotime and gadolinite. The polished metal has a brilliant silvery appearance. It is indispensable for traction magnets (Tb in NdFeB improves thermal resistance) and Terfenol-D magnetostrictive materials. Production is almost exclusively Chinese.",
    appearance: "Polished terbium metal: brilliant silvery ingots, slightly greyish, slowly oxidising on the surface",
    mainMinerals: [
      { name: "Xenotime (with Tb)" },
      { name: "Gadolinite (with Tb)" },
    ],
    spriteLabels: ['Polished terbium metal (silvery)'],
  },
  66: {
    description: "Dysprosium is a rare earth with high neutron absorption capacity, present in xenotime and monazite. Its metal has a slight bluish tint. Essential for high-temperature NdFeB magnets (electric vehicles, wind turbines) and nuclear control rods.",
    appearance: "Dysprosium metal: silvery ingots with subtle bluish tint, slowly oxidising in humid air",
    mainMinerals: [
      { name: "Xenotime (with Dy)" },
      { name: "Monazite (with Dy)" },
    ],
    spriteLabels: ['Dysprosium metal (silvery-blue)'],
  },
  67: {
    description: "Holmium is a rare earth with among the strongest magnetic properties of all elements. Xenotime contains small quantities. Its metal is soft and silvery. Used in magnetic waveguides, medical lasers (Ho:YAG at 2.1 µm) and superconducting magnets.",
    appearance: "Ho-bearing xenotime: pink-brown to reddish-brown prismatic crystals, resinous to vitreous lustre",
    mainMinerals: [
      { name: "Xenotime (with Ho)" },
      { name: "Gadolinite (with Ho)" },
    ],
    spriteLabels: ['Ho xenotime (brown-pink)'],
  },
  68: {
    description: "Erbium is a characteristic pink rare earth whose oxide Er₂O₃ is a distinctive pale-pink pigment. Found in xenotime and gadolinite. Essential for erbium-doped fibre amplifiers (EDFA) enabling long-distance telecommunications.",
    appearance: "Erbium oxide (Er₂O₃): characteristic pale pink powder. Metal: brilliant silvery.",
    mainMinerals: [
      { name: "Xenotime (with Er)" },
      { name: "Gadolinite (with Er)" },
    ],
    spriteLabels: ['Erbium oxide (pale pink)'],
  },
  69: {
    description: "Thulium is the rarest and most expensive natural rare earth. The metal has a brilliant silvery appearance. It is found in minute traces in monazite and xenotime. Used for portable X-ray sources (¹⁷⁰Tm) and solid-state lasers. Production is nearly negligible.",
    appearance: "Thulium metal: very pure brilliant silvery ingots, smooth surface slightly bluish",
    mainMinerals: [
      { name: "Monazite (trace Tm)" },
      { name: "Xenotime (trace Tm)" },
    ],
    spriteLabels: ['Thulium metal (silvery)'],
  },
  70: {
    description: "Ytterbium is a rare earth present in ytterbite (historical gadolinite) and monazite. The dark-brown-blackish gadolinite is its historical source, discovered at Ytterby (Sweden). Used in the most precise atomic clocks, fibre lasers and pressure sensors.",
    appearance: "Gadolinite (ytterbite): compact brown to dark-brownish masses, resinous lustre, often metamict",
    mainMinerals: [
      { name: "Gadolinite (ytterbite)" },
      { name: "Monazite (with Yb)" },
    ],
    spriteLabels: ['Ytterbite / gadolinite (brown)'],
  },
  71: {
    description: "Lutetium is the last and densest rare earth. The silvery-white metal is extracted from monazite and ytterbite. Very expensive due to its extreme rarity. Used in PET-scan scintillators (Lu₂SiO₅:Ce) and refining catalysts.",
    appearance: "Lutetium metal: brilliant silvery-white ingots, one of the densest rare earth metals",
    mainMinerals: [
      { name: "Monazite (trace Lu)" },
      { name: "Gadolinite (trace Lu)" },
    ],
    spriteLabels: ['Lutetium metal (silvery-white)'],
  },
  72: {
    description: "Hafnium is chemically almost identical to zirconium and always present in zircon (1-3%). It is industrially separated from zirconium by fractional distillation. Russia and Australia are the main producers. Used in nuclear reactor control rods.",
    appearance: "Hafniferous zircon: brilliant brown-reddish tetragonal crystals, visually indistinguishable from pure zircon",
    mainMinerals: [
      { name: "Zircon (with Hf)" },
      { name: "Hafnon" },
    ],
    spriteLabels: ['Hafniferous zircon (Hf)'],
  },
  73: {
    description: "Tantalum is found mainly in coltan, a mixture of columbite and tantalite. The DRC (Congo) provides 60% of world production. Tantalum is essential for capacitors in electronic devices (smartphones, computers). Conflict metal.",
    appearance: "Tantalite: black prismatic or tabular crystals with subadamantine lustre, very heavy (density 8)",
    mainMinerals: [
      { name: "Tantalite" },
      { name: "Coltan (colombo-tantalite)" },
      { name: "Microlite" },
    ],
    spriteLabels: ['Coltan/Tantalite (Ta)'],
  },
  74: {
    description: "Tungsten (wolfram) is found in wolframite (brown-black) and scheelite (white, fluorescent under UV). Wolframite is heavy and dark; scheelite glows blue-white under UV lamp. China produces 80% of world tungsten.",
    appearance: "Wolframite: brown-black prismatic crystals, very heavy, with subadamantine metallic lustre",
    mainMinerals: [
      { name: "Wolframite" },
      { name: "Scheelite" },
      { name: "Hübnerite" },
    ],
    spriteLabels: ['Wolframite ((Fe,Mn)WO₄)', 'Scheelite (CaWO₄)'],
  },
  75: {
    description: "Rhenium is one of the rarest elements in the Earth's crust. It is found in trace amounts in molybdenite (MoS₂), from which it is recovered as a by-product. Chile and Kazakhstan are the main producers. It has the 2nd highest melting point of metals (3186°C) after tungsten.",
    appearance: "Rheniferous molybdenite: silvery-grey flakes with metallic lustre, indistinguishable from pure molybdenite",
    mainMinerals: [
      { name: "Rhenium in molybdenite" },
      { name: "Dzhezkazganite" },
    ],
    spriteLabels: ['Rheniferous molybdenite (Re)'],
  },
  76: {
    description: "Osmium is the densest known metal (22.59 g/cm³). It is found in trace amounts in platinum ores as osmiridium (natural Os-Ir alloy). South Africa and Russia are the producers. Its oxide OsO₄ is extremely toxic.",
    appearance: "Native osmium: small grey-blue metallic grains or flakes with a characteristic slight bluish tint",
    mainMinerals: [
      { name: "Native osmium" },
      { name: "Osmiridium (nevyanskite)" },
      { name: "Erlichmanite" },
    ],
    spriteLabels: ['Native osmium (Os)'],
  },
  77: {
    description: "Iridium is the most corrosion-resistant metal. It is found in trace amounts in platinum ores and in the clay layers marking the K-Pg boundary (dinosaur extinction, −66 Ma). South Africa produces 85% of world iridium.",
    appearance: "Native iridium: small very brilliant, extremely hard white-silver grains or nuggets",
    mainMinerals: [
      { name: "Native iridium" },
      { name: "Osmiridium" },
      { name: "Irarsite" },
    ],
    spriteLabels: ['Native iridium (Ir)'],
  },
  78: {
    description: "Platinum is found in native form as grains or nuggets in ultrabasic rocks (dunites, peridotites) and alluvial placers. Russia (Norilsk) and South Africa (Bushveld) provide 80% of production. It is one of the rarest and most precious metals.",
    appearance: "Native platinum: silvery grains or nuggets, very shiny, extremely dense (21.4 g/cm³)",
    mainMinerals: [
      { name: "Native platinum" },
      { name: "Sperrylite" },
      { name: "Cooperite" },
    ],
    spriteLabels: ['Native platinum (Pt)'],
  },
  79: {
    description: "Gold is one of the few metals that is almost always found in native form. It occurs as veins in quartz, alluvial nuggets or microscopic grains in gold-bearing sulfides. China, Australia and Russia are the main producers. Unalterable and recognisable by its unique colour.",
    appearance: "Native gold: nuggets, flakes or filaments of characteristic brilliant yellow, very shiny and dense",
    mainMinerals: [
      { name: "Native gold" },
      { name: "Calaverite (gold telluride)" },
      { name: "Electrum (gold-silver alloy)" },
    ],
    spriteLabels: ['Native gold (Au)'],
  },
  80: {
    description: "Mercury is the only liquid metal at ambient temperature. Its main ore is cinnabar (HgS), a magnificent bright red sulfide. The major mines are at Almadén (Spain, exploited for 3000 years) and in China. Highly toxic, its use is now strictly regulated.",
    appearance: "Cinnabar: bright red trigonal crystals with adamantine lustre. Native mercury: very dense silvery liquid droplets.",
    mainMinerals: [
      { name: "Cinnabar" },
      { name: "Native mercury" },
      { name: "Livingstonite" },
    ],
    spriteLabels: ['Cinnabar (HgS)', 'Metallic mercury (Hg)'],
  },
  81: {
    description: "Thallium is a toxic, rare, heavy metal dispersed in lead and zinc sulfides. Crookesite is its most characteristic ore. Thallium is used in optoelectronics and has been used as a poison (highly toxic, odourless, colourless).",
    appearance: "Crookesite: grey masses with metallic lustre, resembling galena but rarer",
    mainMinerals: [
      { name: "Crookesite" },
      { name: "Lorandite" },
      { name: "Hutchinsonite" },
    ],
    spriteLabels: ['Crookesite (Tl/Cu/Ag Se)'],
  },
  82: {
    description: "Lead is found mainly in galena, a grey metallic cubic lead sulfide, very heavy and shiny. It is one of the most characteristic ores with its perfect cubic cleavage. Galena is also a source of silver. China, Australia and the United States are the main producers.",
    appearance: "Galena: brilliant grey metallic cubic crystals, perfect cubic cleavage, very heavy (7.6 g/cm³)",
    mainMinerals: [
      { name: "Galena" },
      { name: "Cerussite" },
      { name: "Anglesite" },
    ],
    spriteLabels: ['Galena (PbS)'],
  },
  83: {
    description: "Native bismuth forms 'staircase' crystals (hopper crystals) with spectacular iridescent rainbow reflections — red, blue, yellow, green — due to oxidation as a thin surface layer. These crystals are synthesised for collecting, but native bismuth also exists in nature. Unusual and decorative.",
    appearance: "Native bismuth: geometric staircase crystals with spectacular multicoloured iridescent reflections (rainbow)",
    mainMinerals: [
      { name: "Native bismuth" },
      { name: "Bismuthinite" },
      { name: "Bismutite" },
    ],
    spriteLabels: ['Crystallised bismuth (Bi)'],
  },
  84: {
    description: "Polonium was discovered in 1898 by Marie Curie, who named it after her homeland. Extremely radioactive (intense α emitter), 1 gram of ²¹⁰Po generates 140 watts of heat. Used in industrial static eliminators. Famous for the assassination of Alexander Litvinenko in 2006.",
    appearance: "Tiny silvery metallic disc in a shielded container, intense orange-golden radioactive halo",
    mainMinerals: [
      { name: "Uraninite (trace Po)" },
    ],
    spriteLabels: ['Polonium (intense orange halo)'],
  },
  85: {
    description: "Astatine is the rarest natural element on Earth: less than 25 grams exist in the entire Earth's crust. A radioactive halogen (max half-life: 8.1 h for ²¹¹At), it is produced in particle accelerators. Studied for targeted alpha therapy against cancer (²¹¹At coupled to antibodies).",
    appearance: "Ultra-rare iridescent black-violet crystal concept, dark metallic sheen, almost invisible, nanometric fragment",
    mainMinerals: [],
    spriteLabels: ['Astatine (black-violet crystal)'],
  },
  86: {
    description: "Radon is a radioactive (α) noble gas, produced by decay of radium-226 in the uranium chain. It accumulates in basements and cellars of houses built on granite or uranium, constituting a health risk (2nd cause of lung cancer). Half-life: 3.82 days.",
    appearance: "Radioactive colourless gas (emits faint yellow-orange glow under discharge, invisible to the naked eye)",
    mainMinerals: [],
    spriteLabels: ['Rn discharge tube (eerie glow)'],
  },
  87: {
    description: "Francium is the rarest natural element on Earth: at any instant, less than 30 grams exist in the Earth's crust. Produced by decay of actinium-227, it is extremely radioactive (half-life: 22 minutes). Never isolated in macroscopic quantity. The most unstable of the alkali metals.",
    appearance: "Hypothetical silvery alkali metal (never observed in bulk) — artistic concept: metallic droplet in a vial with blue radioactive glow",
    mainMinerals: [],
    spriteLabels: ['Francium — concept (blue glow)'],
  },
  88: {
    description: "Radium was discovered in 1898 by Marie and Pierre Curie in pitchblende. A brilliant white alkaline earth metal, highly radioactive (α and γ), it was used in medicine before its danger was recognised. All radioluminescent radium paint was banned in the 1960s. Ra-226 half-life: 1600 years.",
    appearance: "White luminescent crystals in a sealed glass vial, emitting a ghostly blue-white glow in the dark",
    mainMinerals: [
      { name: "Pitchblende (uraninite)" },
      { name: "Carnotite" },
    ],
    spriteLabels: ['Crystallised radium (blue glow)'],
  },
  89: {
    description: "Actinium was discovered in 1899 by André-Louis Debierne. A brilliant silvery metal, extremely radioactive, it emits a ghostly blue glow in the dark (Cherenkov radiation). Precursor of ²²⁵Ac, promising in nuclear medicine for targeted alpha therapy against cancer.",
    appearance: "Sealed vial with silvery metal emitting a characteristic spectral blue-white glow, visible Cherenkov effect",
    mainMinerals: [
      { name: "Uraninite (trace Ac)" },
    ],
    spriteLabels: ['Actinium (blue Cherenkov glow)'],
  },
  90: {
    description: "Thorium is a weakly radioactive (α) actinide, abundant in monazite and thorite. Its abundance is comparable to that of lead. India, Brazil and Australia have significant deposits. It could constitute an alternative nuclear fuel to uranium.",
    appearance: "Thorite: brown-red to black tetragonal crystals, vitreous to resinous lustre, often metamict",
    mainMinerals: [
      { name: "Thorite" },
      { name: "Monazite (with Th)" },
      { name: "Thorianite" },
    ],
    spriteLabels: ['Thorite (ThSiO₄)'],
  },
  91: {
    description: "Protactinium is a rare and highly radioactive actinide, present in trace amounts in pitchblende (0.34 ppm). A dense silvery metal, it is one of the rarest and most toxic natural elements. Few practical applications due to its radioactivity and rarity. Decay intermediate of uranium-235.",
    appearance: "Brilliant silvery metallic ingot in a hermetic container, slight silvery-blue radioactive halo",
    mainMinerals: [
      { name: "Uraninite (trace Pa)" },
    ],
    spriteLabels: ['Protactinium (silvery ingot)'],
  },
  92: {
    description: "Uranium is found mainly in uraninite (pitchblende), a brilliant black oxide. Colourful secondary minerals such as autunite (fluorescent yellow-green) and torbernite (green) form by weathering in the presence of water. Kazakhstan, Canada and Australia are the main producers.",
    appearance: "Uraninite (pitchblende): greasy black granular masses. Autunite: fluorescent yellow-green crystals under UV.",
    mainMinerals: [
      { name: "Uraninite (pitchblende)" },
      { name: "Autunite" },
      { name: "Carnotite" },
    ],
    spriteLabels: ['Uraninite / Autunite (U)'],
  },
  93: {
    description: "Neptunium is the first transuranium element, synthesised in 1940. Trace amounts are found in uraninite by natural neutron reaction. A slightly golden silvery metal, radioactive. Intermediate in the production of plutonium in nuclear reactors.",
    appearance: "Slightly golden silvery metallic ingot in a shielded container, warm golden radioactive halo",
    mainMinerals: [
      { name: "Uraninite (trace Np)" },
    ],
    spriteLabels: ['Neptunium (silvery-gold ingot)'],
  },
  94: {
    description: "Plutonium is the most famous actinide, sadly known for the atomic bomb. A heavy silvery-golden metal that oxidises on the surface to take on iridescent golden and violet tints. Fuel for fast neutron reactors and space RTG generators (Voyager, Cassini probes). ²³⁹Pu is fissile.",
    appearance: "Sphere or silvery-golden warm metal ingot with slightly iridescent oxidised surface (violet, gold, bronze), amber radioactive halo",
    mainMinerals: [
      { name: "Uraninite (trace Pu)" },
    ],
    spriteLabels: ['Plutonium (iridescent golden sphere)'],
  },
  95: {
    description: "Americium is produced in reactors by neutron capture from plutonium. A silvery metal, it is present in every ionic smoke detector (²⁴¹Am) at approximately 0.3 µg. ²⁴³Am is the source for producing curium. Radioactive α and γ.",
    appearance: "Tiny grey-silver disc in the ionisation chamber of a smoke detector, slight silver-blue halo",
    mainMinerals: [],
    spriteLabels: ['Americium (smoke detector)'],
  },
  96: {
    description: "Curium is named in honour of Marie and Pierre Curie. A brilliant silvery metal produced by plutonium bombardment. Its isotopes ²⁴²Cm and ²⁴⁴Cm generate significant heat, used in RTG generators (Mars Pathfinder probe). Emits a warm orange glow by excitation.",
    appearance: "Glass vial containing a brilliant silvery ingot emitting a warm orange radioactive glow, tribute to the Curies",
    mainMinerals: [],
    spriteLabels: ['Curium (warm orange glow)'],
  },
  97: {
    description: "Berkelium was synthesised in 1949 at Lawrence Berkeley National Laboratory. A silvery metal produced by bombardment of americium. Only a few milligrams have been produced in total. Used exclusively for fundamental research and as a target for synthesising heavier elements (californium, einsteinium).",
    appearance: "Tiny silvery metallic grain on a nuclear research platform, subtle purple radioactive halo",
    mainMinerals: [],
    spriteLabels: ['Berkelium (lab grain, purple halo)'],
  },
  98: {
    description: "Californium is one of the most intense neutron emitters known. A few micrograms of ²⁵²Cf are sufficient to start a nuclear reactor. Used in oil prospecting, treatment of certain cancers by neutron therapy and explosives detection. Produced at Oak Ridge (USA) and Dimitrovgrad (Russia).",
    appearance: "Micro neutron source in a shielded container emitting intense blue-white light, powerful Cherenkov halo",
    mainMinerals: [],
    spriteLabels: ['Californium (neutron source)'],
  },
  99: {
    description: "Einsteinium was discovered in 1952 in the fallout of the first thermonuclear bomb (Ivy Mike). Named in honour of Albert Einstein. Only a few micrograms have ever been produced. The chemical properties of ²⁵³Es were recently studied for the first time (2021, Lawrence Berkeley Lab).",
    appearance: "Artistic concept: tiny pink-red luminescent crystal in a laboratory vial, tribute to Einstein",
    mainMinerals: [],
    spriteLabels: ['Einsteinium (pink-red crystal)'],
  },
  100: {
    description: "Fermium was discovered in 1952 in the same nuclear fallout as einsteinium. Named in honour of Enrico Fermi. Never produced in visible quantity. Practical limit of neutron synthesis: beyond fermium, nuclei fission too rapidly. Only a few hundred atoms have been studied.",
    appearance: "Concept: radioactive green luminescent particles in a laboratory container, tribute to Fermi",
    mainMinerals: [],
    spriteLabels: ['Fermium (green lab glow)'],
  },
  101: {
    description: "Mendelevium was synthesised in 1955 by Ghiorso, Harvey, Choppin, Thompson and Seaborg. Named in honour of Dmitri Mendeleev, creator of the periodic table. Produced atom by atom by bombardment of einsteinium. Only a few tens of atoms have ever been observed.",
    appearance: "Tribute to Mendeleev concept: enigmatic blue-violet glow in a vial, evocation of the periodic table",
    mainMinerals: [],
    spriteLabels: ['Mendelevium (blue-violet glow)'],
  },
  102: {
    description: "Nobelium was synthesised in 1966 (confirmed) by Soviet teams at Dubna. Named in honour of Alfred Nobel. Produced by nuclear fusion of curium and carbon-12. All its isotopes are radioactive, the most stable (²⁵⁹No) having a half-life of 58 minutes. Only a few atoms have been produced.",
    appearance: "Concept: cold crystalline white light in a sealed vial, tribute to Nobel, pale radioactive glow",
    mainMinerals: [],
    spriteLabels: ['Nobelium (cold white glow)'],
  },
  103: {
    description: "Lawrencium is the last actinide, synthesised in 1961 at Lawrence Berkeley Laboratory. Named in honour of Ernest O. Lawrence, inventor of the cyclotron. Produced by bombardment of californium by boron ions. ²⁶²Lr half-life: 3.6 hours. Only a few atoms have been studied.",
    appearance: "Cyclotron concept: swirling gold-white particle beam in a ring, tribute to Lawrence's cyclotron",
    mainMinerals: [],
    spriteLabels: ['Lawrencium (cyclotron beam)'],
  },
  104: {
    description: "Rutherfordium is the first superheavy element (transactinide), synthesised in 1964 at Dubna and 1969 at Berkeley. Named in honour of Ernest Rutherford. ²⁶⁵Rf half-life: 13 hours. Chemical behaviour similar to hafnium. Produced by fusion of curium and neon nuclei.",
    appearance: "Atomic nucleus concept: cross-section of a dense brilliant blue-white nucleus, tribute to the gold foil experiment",
    mainMinerals: [],
    spriteLabels: ['Rutherfordium (blue-white nucleus)'],
  },
  105: {
    description: "Dubnium was synthesised simultaneously at Dubna (Russia) and Berkeley (USA) in 1968-1970, leading to a long priority dispute known as the 'transfermium wars'. Named after the city of Dubna. ²⁶⁸Db half-life: 29 hours. Behaviour similar to tantalum.",
    appearance: "Soviet laboratory concept: cold blue-grey accelerator target disc, Dubna aesthetic with subtle red tint",
    mainMinerals: [],
    spriteLabels: ['Dubnium (accelerator target)'],
  },
  106: {
    description: "Seaborgium was synthesised in 1974 at Berkeley. Named in honour of Glenn T. Seaborg, the only scientist alive to have an element named after him during his lifetime. ²⁶⁹Sg half-life: 14 minutes. Chemical behaviour similar to tungsten.",
    appearance: "Nuclear reaction concept: silver-green energy flash, tribute to Seaborg and transuranium chemistry",
    mainMinerals: [],
    spriteLabels: ['Seaborgium (silver-green flash)'],
  },
  107: {
    description: "Bohrium was synthesised in 1981 at the GSI Helmholtz Centre (Darmstadt, Germany). Named in honour of Niels Bohr. Produced by fusion of bismuth-209 and chromium-54. ²⁷⁰Bh half-life: 61 seconds. Behaviour similar to rhenium.",
    appearance: "Quantum mechanics concept: cyan-white wave interference pattern, evocation of Bohr's atomic model",
    mainMinerals: [],
    spriteLabels: ['Bohrium (cyan quantum waves)'],
  },
  108: {
    description: "Hassium was synthesised in 1984 at GSI (Germany). Named after Hesse (Hassia in Latin). Predicted to be the densest metal ever synthesised (~41 g/cm³). ²⁶⁹Hs half-life: 9.7 seconds. Behaviour similar to osmium. The only transactinide whose volatile oxide has been studied.",
    appearance: "Ultra-dense silvery metal droplet with emerald tint, predicted heaviest metal, German Hesse aesthetic",
    mainMinerals: [],
    spriteLabels: ['Hassium (ultra-dense green metal)'],
  },
  109: {
    description: "Meitnerium was synthesised in 1982 at GSI. Named in honour of Lise Meitner, Austrian physicist co-discoverer of nuclear fission, unjustly overlooked for the Nobel Prize. ²⁷⁸Mt half-life: 7.6 seconds. Predicted behaviour similar to iridium.",
    appearance: "Atomic nucleus splitting in two, elegant pink-gold glow, tribute to Lise Meitner's discovery of fission",
    mainMinerals: [],
    spriteLabels: ['Meitnerium (pink-gold fission)'],
  },
  110: {
    description: "Darmstadtium was synthesised in 1994 at GSI Darmstadt. Named after the city of Darmstadt. Produced by fusion of nickel-62 and lead-208. ²⁸¹Ds half-life: 12.7 seconds. Predicted behaviour similar to platinum. Only a few atoms have been produced.",
    appearance: "Particle beam collision impact point, intense white-silver flash with brilliant corona, GSI Darmstadt",
    mainMinerals: [],
    spriteLabels: ['Darmstadtium (white-silver impact)'],
  },
  111: {
    description: "Roentgenium was synthesised in 1994 at GSI. Named in honour of Wilhelm Röntgen, discoverer of X-rays. ²⁸²Rg half-life: 100 seconds. Predicted behaviour similar to gold but with marked relativistic properties making its chemistry hard to predict.",
    appearance: "X-ray aesthetic: crystal structure illuminated in cold blue-white, evocation of radiography, tribute to Röntgen",
    mainMinerals: [],
    spriteLabels: ['Roentgenium (X-ray aesthetic)'],
  },
  112: {
    description: "Copernicium was synthesised in 1996 at GSI. Named in honour of Nicolas Copernicus. Predicted as a noble gas at ambient temperature despite being a metal — extreme relativistic effect. ²⁸⁵Cn half-life: 28 seconds. The last element whose gas-phase chemistry has been experimentally studied.",
    appearance: "Ethereal silvery-blue vapour swirling like a planetary system, evocation of Copernicus, metal predicted to be gaseous",
    mainMinerals: [],
    spriteLabels: ['Copernicium (silvery-blue vapour)'],
  },
  113: {
    description: "Nihonium was synthesised in 2004 at RIKEN (Japan). First element discovered in Asia. Named after Japan (Nihon in Japanese). ²⁸⁶Nh half-life: 9.5 seconds. Predicted behaviour similar to thallium. Confirmation took 9 years and more than 400 billion fusion attempts.",
    appearance: "Delicate pink-gold atomic glow, RIKEN Japanese laboratory aesthetic, evocation of cherry blossoms at the atomic scale",
    mainMinerals: [],
    spriteLabels: ['Nihonium (Japanese pink-gold glow)'],
  },
  114: {
    description: "Flerovium was synthesised in 1999 at the Flerov Laboratory in Dubna. Named in honour of Georgy Flyorov. Noble gas behaviour predicted by relativistic effects, despite belonging to the lead group. ²⁸⁹Fl half-life: 2.6 seconds. Only a few atoms have been produced.",
    appearance: "Noble-metal gas concept: mysterious violet-silver vapour, Dubna aesthetic, unpredictable behaviour due to relativistic effects",
    mainMinerals: [],
    spriteLabels: ['Flerovium (violet-silver vapour)'],
  },
  115: {
    description: "Moscovium was synthesised in 2003 jointly by Dubna (Russia) and Livermore (USA). Named after Moscow Oblast. ²⁸⁸Mc half-life: 87 milliseconds. Predicted behaviour similar to bismuth. Precursor of nihonium in its decay chain.",
    appearance: "Warm gold-amber radioactive halo, Russian aesthetic, symbolised international scientific collaboration",
    mainMinerals: [],
    spriteLabels: ['Moscovium (gold amber halo)'],
  },
  116: {
    description: "Livermorium was synthesised in 2000 in collaboration between Dubna and Lawrence Livermore National Laboratory (California). Named after Livermore. ²⁹³Lv half-life: 57 milliseconds. Predicted behaviour similar to polonium. Its decay products include flerovium.",
    appearance: "Californian orange-red flash from beam collision, Livermore aesthetic, intense heat of the nuclear reaction",
    mainMinerals: [],
    spriteLabels: ['Livermorium (orange-red flash)'],
  },
  117: {
    description: "Tennessine was synthesised in 2010 in collaboration between Dubna, Oak Ridge, Vanderbilt and Lawrence Livermore. Named after Tennessee. ²⁹⁴Ts half-life: 51 milliseconds. Predicted as a halogen with metallic behaviour. Second-to-last confirmed element of the periodic table.",
    appearance: "Synthetic halogen violet-purple vapour, Tennessee aesthetic (ORNL), last halogens of the periodic table",
    mainMinerals: [],
    spriteLabels: ['Tennessine (violet vapour)'],
  },
  118: {
    description: "Oganesson is the last confirmed element of the periodic table, synthesised in 2002 at Dubna. Named in honour of Yuri Oganessian, father of superheavy elements. ²⁹⁴Og half-life: 0.89 milliseconds. Theoretically a solid noble gas at ambient temperature due to relativistic effects. The frontier of known matter.",
    appearance: "Deep cosmic purple with white quantum sparks, ultimate end of the periodic table, mystery of matter at its limits",
    mainMinerals: [],
    spriteLabels: ['Oganesson (end of table — cosmic)'],
  },
};
